import { cssThemedValue } from '@creditornot/cb-ingredients';
import { Radio } from '@creditornot/cb-radio';
import React, { useMemo } from 'react';
import { Field } from 'react-final-form';
import styled from 'styled-components';

import { SurveyQuestion } from 'apis/merchant-gateway/nps/types';
import { Typography } from 'common/typography';

import { useSurveyIntl } from './SurveyLocaleContext';

type MatrixQuestionProps = {
  question: SurveyQuestion;
  pageIdx: number;
  questionIdx: number;
};

const QuestionHeading = styled(Typography.Body3)`
  display: block;
  margin-block-end: 14px;
`;

const RadioGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AnswersHint = styled.div`
  margin-block-start: 12px;
  display: flex;
  justify-content: space-between;
  color: ${cssThemedValue('textSecondary')};
`;

const SPLIT_CHAR = '-';

const extractExtremeText = (optionText: string): string => {
  if (optionText.includes(SPLIT_CHAR)) {
    const [extractedString] = optionText.split(SPLIT_CHAR);
    return extractedString.trim();
  }
  return '';
};

const MatrixQuestion = ({ question, pageIdx, questionIdx }: MatrixQuestionProps) => {
  const { formatSurveyMessage } = useSurveyIntl();
  const choices = useMemo(() => {
    return (
      question.answers?.choices.map(choice => {
        const optionText = formatSurveyMessage({ fallback: choice.text, resourceId: choice.id });
        const text = optionText.includes(SPLIT_CHAR) ? optionText.split(SPLIT_CHAR)[1] : optionText;
        return {
          ...choice,
          text,
        };
      }) || []
    );
  }, [formatSurveyMessage, question.answers?.choices]);

  const [leftText, rightText] = useMemo(() => {
    const choices = question.answers?.choices;
    const leftTranslatedText = choices
      ? formatSurveyMessage({ fallback: choices[0].text, resourceId: choices[0].id })
      : '';
    const rightTranslatedText = choices
      ? formatSurveyMessage({ fallback: choices[choices.length - 1].text, resourceId: choices[choices.length - 1].id })
      : '';
    return [extractExtremeText(leftTranslatedText), extractExtremeText(rightTranslatedText)];
  }, [formatSurveyMessage, question.answers?.choices]);

  return (
    <>
      <QuestionHeading>
        {formatSurveyMessage({ fallback: question.headings[0].heading, resourceId: question.id })}
      </QuestionHeading>
      {question.answers?.rows.map(row => (
        <RadioGroup key={row.id}>
          {choices.map(choice => (
            <Field
              name={`pages.${pageIdx}.questions.${questionIdx}.answers.0.choiceId`}
              type="radio"
              key={choice.id}
              value={choice.id}
            >
              {({ input }) => <Radio {...input}>{choice.text}</Radio>}
            </Field>
          ))}
        </RadioGroup>
      ))}
      {leftText && rightText && (
        <AnswersHint>
          <Typography.Body3>{leftText}</Typography.Body3>
          <Typography.Body3>{rightText}</Typography.Body3>
        </AnswersHint>
      )}
    </>
  );
};

export default MatrixQuestion;
